import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getAuthToken, refreshAccessToken, setActivityCallback } from "../api/agentApi";

const UserContext = createContext();

function decodeJwt(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return {
      id: decoded["id"],
      username: decoded["sub"],
      role: decoded["role"],
      exp: decoded["exp"],
      token,
    };
  } catch (e) {
    return null;
  }
}

export function UserProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("agentui_user");
    if (saved) return JSON.parse(saved);
    const token = localStorage.getItem("authToken");
    return token ? decodeJwt(token) : null;
  });

  const [passwordSetByOperator, setPasswordSetByOperator] = useState(() => {
    const saved = localStorage.getItem("agentui_passwordSetByOperator");
    return saved ? JSON.parse(saved) : false;
  });

  const [requiresPasswordChange, setRequiresPasswordChange] = useState(() => {
    const saved = localStorage.getItem("agentui_requiresPasswordChange");
    return saved ? JSON.parse(saved) : false;
  });

  const lastActivityRef = useRef(Date.now());
  const refreshInFlight = useRef(false);
  const mountedRef = useRef(true);
  const userSessionActiveRef = useRef(!!user);

  // Update userSessionActiveRef when user changes
  useEffect(() => {
    userSessionActiveRef.current = !!user;
  }, [user]);

  useEffect(() => {
    mountedRef.current = true;

    // Treat any network/UI activity as activity
    const markActive = () => {
      lastActivityRef.current = Date.now();
    };
    setActivityCallback(markActive);

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"]; // coarse but cheap
    activityEvents.forEach(evt => window.addEventListener(evt, markActive));

    const REFRESH_CHECK_MS = 60 * 1000; // check every minute
    const REFRESH_THRESHOLD_SECONDS = 5 * 60; // refresh when <=5 minutes left
    const IDLE_GRACE_MS = 10 * 60 * 1000; // if idle >10 minutes, skip refresh to avoid wasted cycles

    const intervalId = setInterval(async () => {
      // Check if user session is still active using ref to avoid stale closure
      if (!userSessionActiveRef.current) return;
      
      const token = getAuthToken();
      if (!token) return;

      const decoded = decodeJwt(token);
      if (!decoded || !decoded.exp) {
        return;
      }

      const nowSec = Date.now() / 1000;
      const timeLeft = decoded.exp - nowSec;

      const idleMs = Date.now() - lastActivityRef.current;
      const isIdle = idleMs > IDLE_GRACE_MS;

      if (timeLeft <= REFRESH_THRESHOLD_SECONDS && !isIdle && !refreshInFlight.current) {
        refreshInFlight.current = true;
        try {
          const newToken = await refreshAccessToken();
          // Check for both null (401) and undefined (missing access_token)
          if (newToken != null && mountedRef.current) {
            const userObj = decodeJwt(newToken);
            setUser(userObj);
            localStorage.setItem("agentui_user", JSON.stringify(userObj));
            localStorage.setItem("authToken", newToken);
          }
        } catch (e) {
          // Only update auth state if the component is still mounted
          if (!mountedRef.current) {
            return;
          }

          // Distinguish authentication errors from other failures (e.g., network issues)
          const status = (e && e.response && e.response.status) || e?.status;
          if (status === 401 || status === 403) {
            // If refresh fails due to auth, log out to avoid using a stale token
            setUser(null);
            localStorage.removeItem("agentui_user");
            localStorage.removeItem("authToken");
          } else {
            // For non-auth errors (network/transient), keep current auth state
            // Optionally log for diagnostics
            // console.error("Token refresh failed with non-auth error:", e);
          }
        } finally {
          refreshInFlight.current = false;
        }
      }
    }, REFRESH_CHECK_MS);

    return () => {
      mountedRef.current = false;
      setActivityCallback(null);
      activityEvents.forEach(evt => window.removeEventListener(evt, markActive));
      clearInterval(intervalId);
    };
  }, []);

  const login = (token, loginResponse = {}) => {
    const userObj = decodeJwt(token);
    setUser(userObj);
    localStorage.setItem("agentui_user", JSON.stringify(userObj));
    localStorage.setItem("authToken", token);

    // Track password_set_by_operator flag from login response
    const passwordSet = loginResponse.password_set_by_operator || false;
    setPasswordSetByOperator(passwordSet);
    localStorage.setItem("agentui_passwordSetByOperator", JSON.stringify(passwordSet));

    // Determine if password change is required:
    // Only role='user' needs forced password change if password_set_by_operator is true
    const role = loginResponse.role || userObj?.role;
    const requires = passwordSet && role === 'user';
    setRequiresPasswordChange(requires);
    localStorage.setItem("agentui_requiresPasswordChange", JSON.stringify(requires));

    lastActivityRef.current = Date.now();
  };

  const clearPasswordChangeFlag = () => {
    setRequiresPasswordChange(false);
    setPasswordSetByOperator(false);
    localStorage.removeItem("agentui_requiresPasswordChange");
    localStorage.removeItem("agentui_passwordSetByOperator");
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("agentui_user");
    localStorage.removeItem("authToken");
    clearPasswordChangeFlag();
  };

  return (
    <UserContext.Provider value={{
      user,
      login,
      logout,
      passwordSetByOperator,
      requiresPasswordChange,
      clearPasswordChangeFlag
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}
